import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../config/URL";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import GlobalDelete from "../../components/common/GlobalDelete";
import fetchAllCoursesWithIdsC from "../List/CourseListByCenter";
import fetchAllStudentListByCenter from "../List/StudentListByCenter";
import fetchAllPackageListByCenter from "../List/PackageListByCenter";
import { exportVisibleColumnsToCSV } from "../../components/common/ExportCsv";
import { useLocation } from "react-router-dom";

const Invoice = ({ selectedCenter }) => {
  const location = useLocation();

  const paymentStatus = location.state?.paymentStatus || null;
  const storedCenterId = localStorage.getItem("selectedCenterId");

  const [filters, setFilters] = useState({
    centerId: selectedCenter || storedCenterId || "",
    courseId: "",
    studentId: "",
    packageId: "",
    invoiceStatus: paymentStatus || "",
  });
  const [courseData, setCourseData] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  /* -------------------- ROLE MATRIX -------------------- */
  const storedScreens = JSON.parse(localStorage.getItem("screens") || {});
  const canIndex = Boolean(storedScreens?.invoiceIndex);
  const canRead = Boolean(storedScreens?.invoiceRead);
  const canCreate = Boolean(storedScreens?.invoiceCreate);
  const canUpdate = Boolean(storedScreens?.invoiceUpdate);
  const canDelete = Boolean(storedScreens?.invoiceDelete);
  const showActionColumn = canUpdate || canDelete;

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "invoiceStatus",
        header: "Status",
        Cell: ({ row }) => {
          const status = row.original.invoiceStatus?.toUpperCase();
          return status === "PAID" ? (
            <span className="badge bg-success fw-light">Paid</span>
          ) : status === "CANCELLED" ? (
            <span className="badge bg-danger fw-light">Cancelled</span>
          ) : status === "PARTIALLY_PAID" ? (
            <span className="badge bg-info fw-light">Partially Paid</span>
          ) : (
            <span className="badge bg-warning fw-light">Pending</span>
          );
        },
      },
      { accessorKey: "invoiceNumber", header: "Invoice Number" },
      {
        accessorKey: "centerName",
        header: "Centre",
        Cell: ({ row }) =>
          row.original.centerName || row.original.students?.center || "--",
      },
      { accessorKey: "courseName", header: "Course" },
      { accessorKey: "studentUniqueId", header: "Student ID" },
      { accessorKey: "studentName", header: "Student" },
      { accessorKey: "parent", header: "Parent Name" },
      { accessorKey: "packageName", header: "Package" },
      { accessorKey: "outstandingAmount", header: "Outstanding Amount" },
      {
        accessorKey: "invoiceDate",
        header: "Invoice Date",
        Cell: ({ cell }) =>
          cell.getValue()
            ? new Date(cell.getValue()).toLocaleDateString("en-GB")
            : "--",
      },
      { accessorKey: "noOfLessons", header: "Number Of Lesson" },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      { accessorKey: "updatedBy", header: "Updated By" },
    ];

    if (!showActionColumn) return baseColumns;

    const actionColumn = {
      id: "actions",
      header: "",
      size: 20,
      enableSorting: false,
      Cell: ({ row }) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
            setSelectedId(row.original.id);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    };

    return [baseColumns[0], actionColumn, ...baseColumns.slice(1)];
  }, [showActionColumn]);

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });

  /* -------------------- DATA -------------------- */
  const getInvoiceData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();

      // Only add non-empty filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "") {
          queryParams.append(key, value);
        }
      });

      const response = await api.get(
        `/getInvoiceWithCustomInfo${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`,
      );

      setData(response.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch invoices");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      centerId: selectedCenter,
      courseId: "",
      studentId: "",
      packageId: "",
      invoiceStatus: "",
    });
  };

  const fetchListWithCenterIdData = async (centerId) => {
    try {
      const [courseDatas, student, packageData] = await Promise.all([
        fetchAllCoursesWithIdsC(centerId),
        fetchAllStudentListByCenter(centerId),
        fetchAllPackageListByCenter(centerId),
      ]);
      setCourseData(courseDatas || []);
      setStudentData(student || []);
      setPackageData(packageData || []);
    } catch (error) {
      toast.error("Failed to fetch center-specific data");
      console.error(error);
    }
  };

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      centerId: selectedCenter,
    }));
  }, [selectedCenter, paymentStatus]);

  useEffect(() => {
    if (filters.centerId) {
      fetchListWithCenterIdData(filters.centerId);
    }
  }, [filters.centerId]);

  useEffect(() => {
    getInvoiceData();
  }, [filters]);

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedStatus(null);
    setSelectedId(null);
  };

  /* -------------------- MRT INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      columnVisibility: {
        createdBy: false,
        createdAt: false,
        updatedBy: false,
        updatedAt: false,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, data, "InvoiceList.csv")
        }
      >
        Export CSV
      </button>
    ),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: canRead
        ? () => navigate(`/invoice/view/${row.original.id}`)
        : undefined,
      style: { cursor: canRead ? "pointer" : "default" },
    }),
  });

  return (
    <div className="container-fluid center my-4 px-2">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Invoice and Payment
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="active breadcrumb-item" aria-current="page">
          Invoice
        </li>
      </ol>
      <div className="card">
        <div
          className="d-flex align-items-center justify-content-between p-1 mb-3"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted">
            This database shows the list of{" "}
            <strong style={{ color: "#287f71" }}>Invoice</strong>
          </span>
        </div>
        <div className="d-flex flex-column justify-content-between mb-3">
          <div className="d-lg-flex individual_fliters">
            <input type="hidden" name="centerId" value={filters.centerId} />
            <div className="form-group mb-0 mb-1 ms-2">
              <select
                className="form-select form-select-sm center_list"
                name="courseId"
                onChange={handleFilterChange}
                value={filters.courseId}
              >
                <option value="">Select Courses</option>
                {courseData?.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.courseNames}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group mb-0 mb-1 ms-2">
              <select
                className="form-select form-select-sm center_list"
                name="studentId"
                onChange={handleFilterChange}
                value={filters.studentId}
              >
                <option value="">Select Students</option>
                {studentData?.map((std) => (
                  <option key={std.id} value={std.id}>
                    {std.studentNames}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group mb-0 mb-1 ms-2">
              <select
                className="form-select form-select-sm center_list"
                name="invoiceStatus"
                onChange={handleFilterChange}
                value={filters.invoiceStatus}
              >
                <option value="">Select Statuses</option>
                {["PAID", "PENDING", "PARTIALLY_PAID", "CANCELLED"].map(
                  (status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, " ")}
                    </option>
                  ),
                )}
              </select>
            </div>

            <div className="form-group mb-0 mb-1 ms-2">
              <select
                className="form-select form-select-sm center_list"
                name="packageId"
                onChange={handleFilterChange}
                value={filters.packageId}
              >
                <option value="">Select Packages</option>
                {packageData?.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.packageNames}
                  </option>
                ))}
              </select>
            </div>
            {canCreate && (
              <div className="form-group mb-0 mb-1 ms-2">
                <Link to="/invoice/add">
                  <button
                    type="button"
                    className="btn btn-button btn-sm me-2"
                    style={{ fontWeight: "600px !important" }}
                  >
                    Add <i className="bx bx-plus"></i>
                  </button>
                </Link>
              </div>
            )}
          </div>
          <div className="d-lg-flex individual_fliters">
            <div className="form-group mb-2 mx-2">
              <button
                type="button"
                className="btn btn-border btn-sm"
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>

            <Menu
              id="action-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              {canDelete && (
                <MenuItem>
                  <GlobalDelete
                    path={`/deleteGenerateInvoice/${selectedId}`}
                    onDeleteSuccess={getInvoiceData}
                    onOpen={handleMenuClose}
                  />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default Invoice;
